import InboxClient from 'src/clients/InboxClient';
import { NOTIFICATIONS_STATUSES } from 'src/constants';
import { InboxNotificationDetailsResponse } from 'src/entities/Notifications';

import { ApiMethods, ApiTags, appAPI } from '.';

// Keep the first response intentionally small so /notifications can render fast.
const INITIAL_INBOX_NOTIFICATIONS_PAGE_SIZE = 100;
// Use larger pages once UI is already rendered to maximize hydration throughput.
const BACKGROUND_INBOX_NOTIFICATIONS_PAGE_SIZE = 1000;

export interface GetNotificationDetailsBody {
  id: string;
}

export interface UpdateNotificationReadStatusBody {
  isRead: boolean;
}
export interface UpdateNotificationStatusBody {
  notificationId: string;
  status: string;
}

export interface UpdateNotificationBody
  extends UpdateNotificationReadStatusBody {
  id: string;
}

export interface DeleteNotificationBody {
  id: string;
}

export enum DeleteAllNotificationsType {
  ALL = 'ALL',
  READ = 'READ',
}
export interface DeleteAllNotificationsBody {
  type: DeleteAllNotificationsType;
}

export const updateNotificationStatus = (
  notificationsDraft: InboxNotificationDetailsResponse[],
  notificationId: string,
  isRead: boolean,
) =>
  notificationsDraft.map((notification) => {
    if (notification.id === notificationId) {
      notification.isRead = isRead;
    }
    return notification;
  });

export const inboxApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getInboxNotifications: build.query<
      InboxNotificationDetailsResponse[],
      void
    >({
      queryFn: async () => {
        // Return first page immediately so route render does not wait for the
        // full notification history.
        const firstNotificationsPage = await InboxClient.loadNotificationsPage({
          limit: INITIAL_INBOX_NOTIFICATIONS_PAGE_SIZE,
        });

        return {
          data: firstNotificationsPage.notifications,
          meta: {
            nextToken: firstNotificationsPage.nextToken,
          },
        };
      },
      async onQueryStarted(_, lifecycleApi) {
        let nextToken: string | undefined;
        try {
          const queryResult = await lifecycleApi.queryFulfilled;
          nextToken = (queryResult.meta as { nextToken?: string } | undefined)
            ?.nextToken;
        } catch (error) {
          return;
        }

        if (!nextToken) {
          return;
        }

        try {
          await InboxClient.loadRemainingNotificationsPages({
            nextToken,
            limit: BACKGROUND_INBOX_NOTIFICATIONS_PAGE_SIZE,
            onPageLoaded: (notifications) => {
              lifecycleApi.updateCachedData((notificationsDraft) => {
                notificationsDraft.push(...notifications);
              });
            },
          });
        } catch (error) {
          console.error('Failed to load remaining inbox notifications', error);
        }
      },
      providesTags: [ApiTags.inbox_notifications],
    }),
    getInboxNotificationDetails: build.query<
      InboxNotificationDetailsResponse,
      GetNotificationDetailsBody
    >({
      query: ({ id }) => ({
        path: `/notifications/${id}/details`,
        method: ApiMethods.get,
        options: {},
      }),
    }),
    updateInboxNotificationStatus: build.mutation<
      any,
      UpdateNotificationStatusBody
    >({
      query: ({ notificationId, status }) => ({
        path: `/notifications?groupID=${notificationId}`,
        method: ApiMethods.put,
        options: {
          body: {
            status,
          },
        },
      }),
      async onQueryStarted(
        { status, notificationId: updatedNotificationId },
        { dispatch, queryFulfilled },
      ) {
        // optimistically update notification status to read.
        const patchResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxNotifications',
            undefined,
            (notificationsDraft) => {
              updateNotificationStatus(
                notificationsDraft,
                updatedNotificationId,
                status === NOTIFICATIONS_STATUSES.READ,
              );
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
    }),
    deleteAllNotifications: build.mutation<any, DeleteAllNotificationsBody>({
      query: ({ type }) => ({
        path: `/notifications?type=${type}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted({ type }, { dispatch, queryFulfilled }) {
        const delResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxNotifications',
            undefined,
            (draft) => {
              if (type === DeleteAllNotificationsType.ALL) {
                draft.splice(0);
              } else if (type === DeleteAllNotificationsType.READ) {
                const newDraft = draft.filter((d) => !d.isRead);
                draft.splice(0, draft.length, ...newDraft);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          delResult.undo();
        }
      },
    }),
    deleteNotification: build.mutation<any, DeleteNotificationBody>({
      query: ({ id }) => ({
        path: `/notifications/${encodeURIComponent(id)}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const delResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxNotifications',
            undefined,
            (draft) => {
              const index = draft.findIndex((n) => n.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          delResult.undo();
        }
      },
    }),
    updateNotification: build.mutation<any, UpdateNotificationBody>({
      query: ({ id, isRead }) => ({
        path: `/notifications/${encodeURIComponent(id)}?isRead=${isRead}`,
        method: ApiMethods.put,
        options: {},
      }),
      async onQueryStarted({ isRead, id }, { dispatch, queryFulfilled }) {
        // optimistically update notification status
        const patchResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxNotifications',
            undefined,
            (notificationsDraft) => {
              updateNotificationStatus(notificationsDraft, id, isRead);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
    }),
    updateAllNotifications: build.mutation<
      any,
      UpdateNotificationReadStatusBody
    >({
      query: ({ isRead }) => ({
        path: `/notifications/bulk-update?isRead=${isRead}`,
        method: ApiMethods.put,
        options: {},
      }),
      async onQueryStarted({ isRead }, { dispatch, queryFulfilled }) {
        // optimistically update all notifications from list
        const updateResult = dispatch(
          inboxApi.util.updateQueryData(
            'getInboxNotifications',
            undefined,
            (draft) => {
              draft.map((notification) => {
                notification.isRead = isRead;
                return notification;
              });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          updateResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetInboxNotificationsQuery,
  useUpdateInboxNotificationStatusMutation,
  useGetInboxNotificationDetailsQuery,
  useDeleteAllNotificationsMutation,
  useDeleteNotificationMutation,
  useUpdateNotificationMutation,
  useUpdateAllNotificationsMutation,
} = inboxApi;
